import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { extensionCheck } from '../../../modules/environment-checker/checks/extension-check';
import { monitorCheck } from '../../../modules/environment-checker/checks/monitor-check';
import { inspectActiveVideoTrack } from '../../../modules/environment-checker/checks/webcam-device-utils';
import { CheckResult } from '../../../modules/environment-checker/types';
import { CertificationType } from '../../../types/certifications/Certification';
import {
  FOCUS_LOST_DEBOUNCE_MS,
  FOCUS_LOST_LIMIT,
  INITIAL_CAMERA_GRACE_PERIOD_MS,
  INCIDENT_CORRECTION_WINDOW_SECONDS,
  MODERATE_CHECK_INTERVAL_MS,
  QUICK_CHECK_INTERVAL_MS,
} from './constants';
import {
  CriticalFailureCounts,
  CriticalFailureReason,
  InExamCheckStatus,
  ValidationIncident,
} from './types';
import {
  createEmptyCriticalFailureCounts,
  createIncident,
  isCriticalCheckFailure,
  mapCheckIdToReason,
  resolveIncident,
} from './utils';

interface CheckPolicy {
  secondScreenEnabled?: boolean;
  virtualWebcamEnabled?: boolean;
  suspiciousExtensionsEnabled?: boolean;
  screenShareEnabled?: boolean;
}

interface RetryValidationOptions {
  terminateOnFailure?: boolean;
  source?: 'manual' | 'deadline';
}

interface UseExamSecuritySupervisorOptions {
  certificationLogId: string;
  enabled: boolean;
  monitoringType?: CertificationType | null;
  stream: MediaStream | null;
  screenShareActive?: boolean;
  focusLossLimit?: number;
  incidentCorrectionWindowSeconds?: number;
  screenShareIncidentCorrectionWindowSeconds?: number;
  quickCheckIntervalSeconds?: number;
  initialCriticalFailureCounts?: CriticalFailureCounts;
  checkPolicy?: CheckPolicy;
  onFocusLossNotice?: (incident: ValidationIncident) => void;
  onIncidentOpen: (incident: ValidationIncident) => void;
  onIncidentResolved: (incident: ValidationIncident) => void;
  onTerminateExam: (incident: ValidationIncident) => void;
  onStatusChange: (status: InExamCheckStatus) => void;
  prepareRetryValidation?: (options: RetryValidationOptions) => Promise<void>;
}

interface SupervisorState {
  activeIncident: ValidationIncident | null;
  countdown: number;
  statusLabel: string;
  status: InExamCheckStatus;
  retryValidation: (options?: RetryValidationOptions) => Promise<void>;
  reportMonitoringFailure: (message: string) => void;
}

async function createCameraCheck(
  stream: MediaStream | null,
  virtualWebcamEnabled = true,
): Promise<CheckResult> {
  const timestamp = new Date().toISOString();
  const videoTrack = stream?.getVideoTracks?.()[0];

  if (!stream || !videoTrack || videoTrack.readyState === 'ended') {
    return {
      checkId: 'camera_stream',
      checkName: 'CÃ¢mera ativa',
      status: 'failed',
      details: 'O vÃ­deo da webcam foi interrompido.',
      timestamp,
    };
  }

  if (videoTrack.muted || !videoTrack.enabled) {
    return {
      checkId: 'camera_stream',
      checkName: 'CÃ¢mera ativa',
      status: 'warning',
      details: 'A webcam estÃ¡ reconectando ou temporariamente indisponÃ­vel.',
      timestamp,
    };
  }

  const devices = navigator.mediaDevices?.enumerateDevices
    ? await navigator.mediaDevices.enumerateDevices()
    : [];
  const inspection = await inspectActiveVideoTrack(videoTrack, devices);

  if (virtualWebcamEnabled && inspection.activeDeviceIsVirtual === true) {
    return {
      checkId: 'webcam_virtual',
      checkName: 'CÃ¢mera ativa',
      status: 'failed',
      details: `CÃ¢mera virtual em uso detectada durante a prova: "${
        inspection.activeDeviceLabel || 'CÃ¢mera virtual'
      }". Selecione uma webcam fÃ­sica e revalide.`,
      timestamp,
      metadata: {
        activeDeviceLabel: inspection.activeDeviceLabel,
        activeDeviceId: inspection.activeDeviceId,
        activeDeviceIsVirtual: inspection.activeDeviceIsVirtual,
        installedVirtualDevices: inspection.installedVirtualDevices,
        detectionSource: inspection.detectionSource,
      },
    };
  }

  if (inspection.activeDeviceIsVirtual === undefined) {
    return {
      checkId: 'camera_stream',
      checkName: 'CÃ¢mera ativa',
      status: 'warning',
      details:
        'NÃ£o foi possÃ­vel confirmar se a cÃ¢mera ativa continua fÃ­sica. Revalide mantendo a webcam selecionada.',
      timestamp,
      metadata: {
        activeDeviceLabel: inspection.activeDeviceLabel,
        activeDeviceId: inspection.activeDeviceId,
        activeDeviceIsVirtual: inspection.activeDeviceIsVirtual,
        installedVirtualDevices: inspection.installedVirtualDevices,
        detectionSource: inspection.detectionSource,
      },
    };
  }

  return {
    checkId: 'camera_stream',
    checkName: 'CÃ¢mera ativa',
    status: 'passed',
    details: 'Webcam ativa e transmitindo.',
    timestamp,
    metadata: {
      activeDeviceLabel: inspection.activeDeviceLabel,
      activeDeviceId: inspection.activeDeviceId,
      activeDeviceIsVirtual: inspection.activeDeviceIsVirtual,
      installedVirtualDevices: inspection.installedVirtualDevices,
      detectionSource: inspection.detectionSource,
    },
  };
}

function createFocusCheck(): CheckResult {
  const timestamp = new Date().toISOString();
  const { hidden } = document;
  const focused = document.hasFocus();

  if (hidden || !focused) {
    return {
      checkId: 'browser_focus',
      checkName: 'Foco da prova',
      status: 'failed',
      details:
        'A prova perdeu o foco ou a aba ficou oculta. Retorne imediatamente à  prova.',
      timestamp,
      metadata: {
        hidden,
        focused,
      },
    };
  }

  return {
    checkId: 'browser_focus',
    checkName: 'Foco da prova',
    status: 'passed',
    details: 'A prova está em foco.',
    timestamp,
  };
}

function requiresCamera(monitoringType?: CertificationType | null) {
  return monitoringType === 'video' || monitoringType === 'video_liveness';
}

function createScreenShareCheck(active: boolean): CheckResult {
  const timestamp = new Date().toISOString();

  if (!active) {
    return {
      checkId: 'screen_share_active',
      checkName: 'Compartilhamento de tela',
      status: 'failed',
      details:
        'O compartilhamento de tela foi interrompido. Retome o compartilhamento da tela inteira.',
      timestamp,
    };
  }

  return {
    checkId: 'screen_share_active',
    checkName: 'Compartilhamento de tela',
    status: 'passed',
    details: 'Compartilhamento de tela ativo.',
    timestamp,
  };
}

function usesUnifiedInfractionLabel(reason?: CriticalFailureReason | null) {
  return (
    reason === 'browser_focus_lost' ||
    reason === 'monitoring_failure' ||
    reason === 'extra_monitor' ||
    reason === 'screen_share_stopped'
  );
}

function isCameraCriticalReason(reason?: CriticalFailureReason | null) {
  return reason === 'camera_stream_lost' || reason === 'virtual_camera_in_use';
}

export function useExamSecuritySupervisor({
  certificationLogId,
  enabled,
  monitoringType,
  stream,
  screenShareActive,
  focusLossLimit,
  incidentCorrectionWindowSeconds,
  screenShareIncidentCorrectionWindowSeconds,
  quickCheckIntervalSeconds,
  initialCriticalFailureCounts,
  checkPolicy,
  onFocusLossNotice,
  onIncidentOpen,
  onIncidentResolved,
  onTerminateExam,
  onStatusChange,
  prepareRetryValidation,
}: UseExamSecuritySupervisorOptions): SupervisorState {
  const resolvedFocusLossLimit = Math.max(
    1,
    Math.round(focusLossLimit ?? FOCUS_LOST_LIMIT),
  );
  const resolvedIncidentCorrectionWindowSeconds = Math.max(
    1,
    Math.round(
      incidentCorrectionWindowSeconds ?? INCIDENT_CORRECTION_WINDOW_SECONDS,
    ),
  );
  const resolvedScreenShareIncidentCorrectionWindowSeconds = Math.max(
    1,
    Math.round(screenShareIncidentCorrectionWindowSeconds ?? 15),
  );
  const resolvedQuickCheckIntervalMs = Math.max(
    1000,
    Math.round(
      (quickCheckIntervalSeconds ?? QUICK_CHECK_INTERVAL_MS / 1000) * 1000,
    ),
  );

  const attemptsRef = useRef<CriticalFailureCounts>(
    createEmptyCriticalFailureCounts(),
  );
  const screenShareActiveRef = useRef(screenShareActive ?? false);
  const previousScreenShareActiveRef = useRef(screenShareActive ?? false);
  const hasObservedCameraStreamRef = useRef(false);
  const initialCameraGraceDeadlineRef = useRef<number | null>(null);
  const incidentRef = useRef<ValidationIncident | null>(null);
  const retryPreparationInProgressRef = useRef(false);
  const statusRef = useRef<InExamCheckStatus>('idle');
  const retryValidationRef = useRef<() => Promise<void>>(async () => undefined);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval>>();
  const quickIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const moderateIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const focusDebounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const focusLossEpisodeActiveRef = useRef(false);
  const onIncidentOpenRef = useRef(onIncidentOpen);
  const onFocusLossNoticeRef = useRef(onFocusLossNotice);
  const onIncidentResolvedRef = useRef(onIncidentResolved);
  const onTerminateExamRef = useRef(onTerminateExam);
  const onStatusChangeRef = useRef(onStatusChange);
  const [activeIncident, setActiveIncident] =
    useState<ValidationIncident | null>(null);
  const [countdown, setCountdown] = useState(
    resolvedIncidentCorrectionWindowSeconds,
  );
  const [status, setStatus] = useState<InExamCheckStatus>('idle');

  const statusLabel = useMemo(() => {
    if (
      usesUnifiedInfractionLabel(activeIncident?.reason) &&
      activeIncident?.limit
    ) {
      return `InfraÃ§Ã£o ${activeIncident.occurrence}/${activeIncident.limit}`;
    }
    if (activeIncident) return activeIncident.reasonLabel;
    if (status === 'recovering') return 'Reconectando';
    if (status === 'warning') return 'Monitoramento com aviso';
    if (status === 'approved') return 'Monitoramento ativo';
    if (!enabled) return 'Monitoramento aguardando inÃ­cio';
    return 'CÃ¢mera ativa';
  }, [activeIncident, enabled, status]);

  const updateStatus = useCallback((nextStatus: InExamCheckStatus) => {
    if (statusRef.current === nextStatus) return;

    statusRef.current = nextStatus;
    setStatus(nextStatus);
    onStatusChangeRef.current(nextStatus);
  }, []);

  const getIncidentCorrectionWindowSeconds = useCallback(
    (reason?: CriticalFailureReason | null) => {
      if (reason === 'screen_share_stopped') {
        return resolvedScreenShareIncidentCorrectionWindowSeconds;
      }

      return resolvedIncidentCorrectionWindowSeconds;
    },
    [
      resolvedIncidentCorrectionWindowSeconds,
      resolvedScreenShareIncidentCorrectionWindowSeconds,
    ],
  );

  const resetCountdown = useCallback(
    (reason?: CriticalFailureReason | null) => {
      setCountdown(getIncidentCorrectionWindowSeconds(reason));
    },
    [getIncidentCorrectionWindowSeconds],
  );

  useEffect(() => {
    onIncidentOpenRef.current = onIncidentOpen;
    onFocusLossNoticeRef.current = onFocusLossNotice;
    onIncidentResolvedRef.current = onIncidentResolved;
    onTerminateExamRef.current = onTerminateExam;
    onStatusChangeRef.current = onStatusChange;
  }, [
    onFocusLossNotice,
    onIncidentOpen,
    onIncidentResolved,
    onTerminateExam,
    onStatusChange,
  ]);

  useEffect(() => {
    const nextCounts = {
      ...createEmptyCriticalFailureCounts(),
      ...initialCriticalFailureCounts,
    };

    (Object.keys(nextCounts) as CriticalFailureReason[]).forEach(reason => {
      attemptsRef.current[reason] = Math.max(
        attemptsRef.current[reason] ?? 0,
        nextCounts[reason],
      );
    });
  }, [initialCriticalFailureCounts]);

  useEffect(() => {
    screenShareActiveRef.current = screenShareActive ?? false;
  }, [screenShareActive]);

  useEffect(() => {
    if (!requiresCamera(monitoringType)) {
      hasObservedCameraStreamRef.current = false;
      initialCameraGraceDeadlineRef.current = null;
      return;
    }

    const videoTrack = stream?.getVideoTracks?.()[0];
    if (stream && videoTrack && videoTrack.readyState !== 'ended') {
      hasObservedCameraStreamRef.current = true;
      initialCameraGraceDeadlineRef.current = null;
      return;
    }

    if (enabled && initialCameraGraceDeadlineRef.current === null) {
      initialCameraGraceDeadlineRef.current =
        Date.now() + INITIAL_CAMERA_GRACE_PERIOD_MS;
    }
  }, [enabled, monitoringType, stream]);

  const clearCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = undefined;
    }
  }, []);

  const clearFocusDebounce = useCallback(() => {
    if (focusDebounceTimerRef.current) {
      clearTimeout(focusDebounceTimerRef.current);
      focusDebounceTimerRef.current = undefined;
    }
  }, []);

  const terminateExam = useCallback(
    (incident: ValidationIncident) => {
      const terminatedIncident: ValidationIncident = {
        ...incident,
        status: 'terminated',
      };
      clearCountdown();
      clearFocusDebounce();
      setActiveIncident(terminatedIncident);
      incidentRef.current = terminatedIncident;
      updateStatus('terminated');
      onTerminateExamRef.current(terminatedIncident);
    },
    [clearCountdown, clearFocusDebounce, updateStatus],
  );

  const openIncident = useCallback(
    (check: CheckResult) => {
      const reason = mapCheckIdToReason(check.checkId);
      const currentAttempt = (attemptsRef.current[reason] ?? 0) + 1;
      attemptsRef.current[reason] = currentAttempt;
      const occurrence = usesUnifiedInfractionLabel(reason)
        ? attemptsRef.current.browser_focus_lost +
          attemptsRef.current.monitoring_failure +
          attemptsRef.current.extra_monitor +
          attemptsRef.current.screen_share_stopped
        : currentAttempt;

      const incident = createIncident(
        certificationLogId,
        check.checkId,
        check.details,
        occurrence,
        usesUnifiedInfractionLabel(reason) ? currentAttempt : undefined,
        resolvedFocusLossLimit,
      );

      const shouldTerminate = usesUnifiedInfractionLabel(reason)
        ? occurrence >= resolvedFocusLossLimit
        : currentAttempt > 1;

      if (shouldTerminate) {
        terminateExam(incident);
        return;
      }

      if (reason === 'browser_focus_lost' || reason === 'monitoring_failure') {
        if (reason === 'browser_focus_lost') {
          focusLossEpisodeActiveRef.current = true;
        }
        clearFocusDebounce();
        updateStatus('warning');
        onFocusLossNoticeRef.current?.(incident);
        return;
      }

      clearFocusDebounce();
      setActiveIncident(incident);
      incidentRef.current = incident;
      updateStatus('critical');
      onIncidentOpenRef.current(incident);
      clearCountdown();
      resetCountdown(reason);

      countdownTimerRef.current = setInterval(() => {
        let shouldRetryValidation = false;

        setCountdown(previousCountdown => {
          if (previousCountdown <= 1) {
            shouldRetryValidation = true;
            return 0;
          }

          return previousCountdown - 1;
        });

        if (shouldRetryValidation) {
          clearCountdown();
          retryValidationRef.current().catch(() => undefined);
        }
      }, 1000);
    },
    [
      certificationLogId,
      clearCountdown,
      clearFocusDebounce,
      resetCountdown,
      resolvedFocusLossLimit,
      terminateExam,
      updateStatus,
    ],
  );

  const resolveOpenIncident = useCallback(() => {
    if (!incidentRef.current) return;

    const resolved = resolveIncident(incidentRef.current);
    setActiveIncident(null);
    incidentRef.current = null;
    clearCountdown();
    resetCountdown();
    updateStatus('approved');
    onIncidentResolvedRef.current(resolved);
  }, [clearCountdown, resetCountdown, updateStatus]);

  const runValidation = useCallback(
    async (mode: 'quick' | 'moderate') => {
      if (!enabled) return;
      if (retryPreparationInProgressRef.current) return;

      const secondScreenEnabled = checkPolicy?.secondScreenEnabled !== false;
      const virtualWebcamEnabled = checkPolicy?.virtualWebcamEnabled !== false;
      const extensionsEnabled =
        checkPolicy?.suspiciousExtensionsEnabled !== false;

      const checks: Array<Promise<CheckResult> | CheckResult> = [
        createFocusCheck(),
      ];

      if (secondScreenEnabled) {
        checks.push(monitorCheck());
      }

      const screenShareEnabled = checkPolicy?.screenShareEnabled === true;
      if (screenShareEnabled) {
        checks.push(createScreenShareCheck(screenShareActiveRef.current));
      }

      const shouldCheckCamera =
        requiresCamera(monitoringType) &&
        (hasObservedCameraStreamRef.current ||
          Boolean(stream) ||
          (initialCameraGraceDeadlineRef.current !== null &&
            Date.now() >= initialCameraGraceDeadlineRef.current));

      if (shouldCheckCamera) {
        checks.push(createCameraCheck(stream, virtualWebcamEnabled));
      }

      if (mode === 'moderate' && extensionsEnabled) {
        checks.push(extensionCheck());
      }

      const results = await Promise.all(checks);
      const criticalResult = results.find(
        result =>
          result.checkId !== 'browser_focus' &&
          isCriticalCheckFailure(result.checkId, result.status),
      );
      const focusResult = results.find(
        result =>
          result.checkId === 'browser_focus' &&
          isCriticalCheckFailure(result.checkId, result.status),
      );

      if (criticalResult) {
        clearFocusDebounce();
        const currentIncidentReason = incidentRef.current
          ? mapCheckIdToReason(incidentRef.current.checkId)
          : null;
        const criticalResultReason = mapCheckIdToReason(criticalResult.checkId);

        if (
          incidentRef.current &&
          (currentIncidentReason === criticalResultReason ||
            (isCameraCriticalReason(currentIncidentReason) &&
              isCameraCriticalReason(criticalResultReason)))
        ) {
          updateStatus('critical');
          return;
        }

        openIncident(criticalResult);
        return;
      }

      if (focusResult) {
        if (focusLossEpisodeActiveRef.current) {
          updateStatus('warning');
          return;
        }

        if (
          incidentRef.current &&
          incidentRef.current.reason === 'browser_focus_lost'
        ) {
          updateStatus('critical');
          return;
        }

        if (!focusDebounceTimerRef.current) {
          focusDebounceTimerRef.current = setTimeout(() => {
            focusDebounceTimerRef.current = undefined;

            const confirmedFocusCheck = createFocusCheck();
            if (
              isCriticalCheckFailure(
                confirmedFocusCheck.checkId,
                confirmedFocusCheck.status,
              )
            ) {
              openIncident(confirmedFocusCheck);
            }
          }, FOCUS_LOST_DEBOUNCE_MS);
        }
        return;
      }

      clearFocusDebounce();
      focusLossEpisodeActiveRef.current = false;
      const hasWarning = results.some(result => result.status === 'warning');

      if (incidentRef.current) {
        resolveOpenIncident();
        return;
      }

      updateStatus(hasWarning ? 'warning' : 'approved');
    },
    [
      checkPolicy,
      clearFocusDebounce,
      enabled,
      monitoringType,
      openIncident,
      resolveOpenIncident,
      stream,
      updateStatus,
    ],
  );

  const retryValidation = useCallback(
    async ({
      terminateOnFailure = false,
      source = 'manual',
    }: RetryValidationOptions = {}) => {
      if (!enabled) return;

      const previousIncident = incidentRef.current;
      retryPreparationInProgressRef.current = true;

      try {
        await prepareRetryValidation?.({ terminateOnFailure, source });
      } finally {
        retryPreparationInProgressRef.current = false;
      }

      clearFocusDebounce();
      updateStatus('recovering');
      await runValidation('moderate');

      if (terminateOnFailure && incidentRef.current) {
        terminateExam(incidentRef.current);
      }

      const currentIncident = incidentRef.current;
      const currentStatus = statusRef.current;
      let result: 'terminated' | 'failed' | 'resolved' = 'resolved';

      if (currentStatus === 'terminated') {
        result = 'terminated';
      } else if (currentIncident) {
        result = 'failed';
      }

      // eslint-disable-next-line no-console
      console.log('[Exam][monitoring][revalidation_result]', {
        source,
        result,
        certificationLogId,
        monitoringType: monitoringType ?? 'none',
        previousIncidentReason: previousIncident?.reason ?? null,
        previousIncidentCheckId: previousIncident?.checkId ?? null,
        currentIncidentReason: currentIncident?.reason ?? null,
        currentIncidentCheckId: currentIncident?.checkId ?? null,
        status: currentStatus,
        terminateOnFailure,
      });
    },
    [
      certificationLogId,
      clearFocusDebounce,
      enabled,
      monitoringType,
      prepareRetryValidation,
      runValidation,
      terminateExam,
      updateStatus,
    ],
  );

  retryValidationRef.current = () =>
    retryValidation({ terminateOnFailure: true, source: 'deadline' });

  const reportMonitoringFailure = useCallback(
    (message: string) => {
      openIncident({
        checkId: 'monitoring_liveness',
        checkName: 'Monitoramento inteligente',
        status: 'failed',
        details: message,
        timestamp: new Date().toISOString(),
      });
    },
    [openIncident],
  );

  useEffect(() => {
    if (!enabled) {
      hasObservedCameraStreamRef.current = false;
      initialCameraGraceDeadlineRef.current = null;
      focusLossEpisodeActiveRef.current = false;
      clearCountdown();
      resetCountdown();
      clearFocusDebounce();
      setActiveIncident(null);
      incidentRef.current = null;
      updateStatus('idle');
      return undefined;
    }

    runValidation('moderate').catch(() => undefined);

    quickIntervalRef.current = setInterval(() => {
      runValidation('quick').catch(() => undefined);
    }, resolvedQuickCheckIntervalMs);

    moderateIntervalRef.current = setInterval(() => {
      runValidation('moderate').catch(() => undefined);
    }, MODERATE_CHECK_INTERVAL_MS);

    const rerunValidation = () => {
      runValidation('moderate').catch(() => undefined);
    };

    window.addEventListener('visibilitychange', rerunValidation);
    window.addEventListener('focus', rerunValidation);
    window.addEventListener('blur', rerunValidation);
    window.addEventListener('resize', rerunValidation);
    navigator.mediaDevices?.addEventListener?.('devicechange', rerunValidation);

    return () => {
      clearFocusDebounce();

      if (quickIntervalRef.current) {
        clearInterval(quickIntervalRef.current);
        quickIntervalRef.current = undefined;
      }
      if (moderateIntervalRef.current) {
        clearInterval(moderateIntervalRef.current);
        moderateIntervalRef.current = undefined;
      }

      window.removeEventListener('visibilitychange', rerunValidation);
      window.removeEventListener('focus', rerunValidation);
      window.removeEventListener('blur', rerunValidation);
      window.removeEventListener('resize', rerunValidation);
      navigator.mediaDevices?.removeEventListener?.(
        'devicechange',
        rerunValidation,
      );
    };
  }, [
    clearCountdown,
    clearFocusDebounce,
    enabled,
    resetCountdown,
    resolvedQuickCheckIntervalMs,
    runValidation,
    updateStatus,
  ]);

  useEffect(() => {
    const previousValue = previousScreenShareActiveRef.current;
    const nextValue = screenShareActive ?? false;
    previousScreenShareActiveRef.current = nextValue;

    if (!enabled || checkPolicy?.screenShareEnabled !== true) {
      return;
    }

    if (previousValue === nextValue) {
      return;
    }

    runValidation('quick').catch(() => undefined);
  }, [
    checkPolicy?.screenShareEnabled,
    enabled,
    runValidation,
    screenShareActive,
  ]);

  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = undefined;
      }

      if (focusDebounceTimerRef.current) {
        clearTimeout(focusDebounceTimerRef.current);
        focusDebounceTimerRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (!enabled || !stream) return undefined;

    const tracks = stream.getVideoTracks();
    const rerunValidation = () => {
      runValidation('quick').catch(() => undefined);
    };

    tracks.forEach(track => {
      track.addEventListener('ended', rerunValidation);
      track.addEventListener('mute', rerunValidation);
      track.addEventListener('unmute', rerunValidation);
    });

    return () => {
      tracks.forEach(track => {
        track.removeEventListener('ended', rerunValidation);
        track.removeEventListener('mute', rerunValidation);
        track.removeEventListener('unmute', rerunValidation);
      });
    };
  }, [enabled, runValidation, stream]);

  return {
    activeIncident,
    countdown,
    statusLabel,
    status,
    retryValidation,
    reportMonitoringFailure,
  };
}
